/**
 * The airship's rooms as walking geometry (ADR 0033, lifted from brad-airship
 * `airship-layout.js` `buildLayout` and `docs/SHIP-DATA-MODEL.md`). The room
 * grid is the deck scene's `ship` block, the room types are
 * `rules/ship_rooms.yaml`; from them this module derives, once, for every
 * room: where it lies (`shipRoomExtent`), its walls split around its doors,
 * its doors, its floor, and the transforms between the room's own space and
 * the scene's. The renderer's room prefabs draw from the same layout, so the
 * walls a body bumps into are the walls on screen (the prototype's one layout
 * authority).
 *
 * Axes are game axes: x east, y north, metres. A room's own space is the
 * unturned room's, its origin the room's centre; a turn of `rotation` degrees
 * clockwise (seen from above) takes (x, y) to (y, −x) per quarter, as the
 * prototype's (x, z) → (−z, x) with z = −y. A door's offset counts cells from
 * the wall's west end (north and south walls) or north end (east and west
 * walls); its centre is (offset + ½) cells along, its clear width the type's
 * `doorWidth`.
 *
 * The walking colliders of a ship scene (`shipWalkExtras`) are its rooms'
 * walls plus every solid furnishing (an interactable with a `size`, its
 * footprint turned with the room it stands in); the walkable ground adds each
 * room's floor. Content lint checks the grid, the types, levels and doors, so
 * a room this module would build wrong never loads.
 */
import { type Scene, type ShipPassage, type ShipRoom, type ShipRoomExtent, type ShipRoomRules, type ShipSide, shipPassages, shipRoomExtent, shipSides } from '@ab/content-schema';
import type { Point } from './map.ts';
import type { SceneState } from './state.ts';

type Rect = { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number };

export type ShipWall = Rect & { readonly id: string; readonly side: ShipSide; readonly height: number };
export type ShipDoor = { readonly id: string; readonly side: ShipSide; readonly offset: number; readonly width: number; readonly x: number; readonly y: number };

export type ShipRoomLayout = {
  readonly room: ShipRoom;
  readonly type: ShipRoomRules[string];
  readonly level: ShipRoomRules[string]['levels'][number];
  /** The unturned room's size along its own x (width) and y (depth), metres. */
  readonly width: number;
  readonly depth: number;
  /** Where it lies in the scene: its turned extent, centre and floor height. */
  readonly extent: ShipRoomExtent;
  /** In the room's own space. */
  readonly walls: readonly ShipWall[];
  readonly doors: readonly ShipDoor[];
  /** In the scene's space: the walls turned, and each door's centre and side turned. */
  readonly worldWalls: readonly ShipWall[];
  readonly worldDoors: readonly ShipDoor[];
  /** Its doors as passages, in the scene's space (a hatch to another deck or a doorway into the next room; `shipPassages`). */
  readonly passages: readonly ShipPassage[];
  toWorld(p: Point): Point;
  toLocal(p: Point): Point;
};

const quarterTurn = (side: ShipSide, quarters: number): ShipSide => shipSides[(shipSides.indexOf(side) + quarters) % 4] ?? side;

/** The room's walls in its own space, split around its doors (`airship-layout.js` `localWalls`). */
const localWalls = (room: ShipRoom, width: number, depth: number, cell: number, wall: ShipRoomRules[string]['wall']): { walls: ShipWall[]; doors: ShipDoor[] } => {
  const walls: ShipWall[] = [];
  const doors: ShipDoor[] = [];
  const t = wall.thickness;
  for (const side of shipSides) {
    const horizontal = side === 'north' || side === 'south';
    const extent = horizontal ? width : depth;
    // Along the wall from its west (north, south walls) or north (east, west walls) end.
    const cuts = room.doors
      .filter((d) => d.side === side)
      .map((d) => {
        const along = -extent / 2 + (d.offset + 0.5) * cell;
        const x = horizontal ? along : side === 'west' ? -width / 2 : width / 2;
        const y = horizontal ? (side === 'north' ? depth / 2 : -depth / 2) : -along;
        doors.push({ id: `${room.id}_door_${side}_${d.offset}`, side, offset: d.offset, width: wall.doorWidth, x, y });
        return [along - wall.doorWidth / 2, along + wall.doorWidth / 2] as const;
      })
      .sort((a, b) => a[0] - b[0]);
    const edge = horizontal ? (side === 'north' ? depth / 2 : -depth / 2) : side === 'west' ? -width / 2 : width / 2;
    let from = -extent / 2;
    const segment = (to: number): void => {
      if (to <= from) return;
      const id = `${room.id}_wall_${side}_${walls.length}`;
      // Along a north or south wall `from..to` runs west to east; along an east or west wall north to south (y falls).
      walls.push(horizontal ? { id, side, height: wall.height, x0: from, x1: to, y0: edge - t / 2, y1: edge + t / 2 } : { id, side, height: wall.height, x0: edge - t / 2, x1: edge + t / 2, y0: -to, y1: -from });
    };
    for (const [start, end] of cuts) {
      segment(start);
      from = end;
    }
    segment(extent / 2);
  }
  return { walls, doors };
};

/** Every room of a ship (the scene with the `ship` block), laid out; a room whose type or level the rules lack is left out (content lint reports it). */
export const shipLayout = (ship: Scene, rules: ShipRoomRules): ShipRoomLayout[] => {
  const grid = ship.ship?.grid;
  if (grid === undefined || ship.ship === undefined) return [];
  const passages = shipPassages(ship.ship, rules);
  return ship.ship.rooms.flatMap((room): ShipRoomLayout[] => {
    const type = rules[room.type];
    const level = type?.levels.find((l) => l.level === room.level);
    const extent = shipRoomExtent(grid, room);
    if (type === undefined || level === undefined || extent === null) return [];
    const width = room.size[0] * grid.cell;
    const depth = room.size[1] * grid.cell;
    const quarters = room.rotation / 90;
    const turn = (p: Point, n: number): Point => {
      let { x, y } = p;
      for (let q = 0; q < n; q += 1) [x, y] = [y, -x];
      return { x, y };
    };
    const toWorld = (p: Point): Point => {
      const r = turn(p, quarters);
      return { x: r.x + extent.cx, y: r.y + extent.cy };
    };
    const toLocal = (p: Point): Point => turn({ x: p.x - extent.cx, y: p.y - extent.cy }, (4 - quarters) % 4);
    const rectToWorld = <R extends Rect>(r: R): R => {
      const a = toWorld({ x: r.x0, y: r.y0 });
      const b = toWorld({ x: r.x1, y: r.y1 });
      return { ...r, x0: Math.min(a.x, b.x), x1: Math.max(a.x, b.x), y0: Math.min(a.y, b.y), y1: Math.max(a.y, b.y) };
    };
    const { walls, doors } = localWalls(room, width, depth, grid.cell, type.wall);
    return [
      {
        room,
        type,
        level,
        width,
        depth,
        extent,
        walls,
        doors,
        worldWalls: walls.map((w) => ({ ...rectToWorld(w), side: quarterTurn(w.side, quarters) })),
        worldDoors: doors.map((d) => ({ ...d, ...toWorld(d), side: quarterTurn(d.side, quarters) })),
        passages: passages.filter((p) => p.room === room.id),
        toWorld,
        toLocal,
      },
    ];
  });
};

/** The rooms a ship scene shows: its deck's (or the ones it names) of the ship it is on. */
export const sceneRooms = (scene: Scene, ship: Scene, rules: ShipRoomRules): ShipRoomLayout[] => {
  const on = scene.onShip;
  if (on === undefined) return [];
  return shipLayout(ship, rules).filter((r) => r.room.deck === on.deck && (on.rooms === undefined || on.rooms.includes(r.room.id)));
};

/** The room an interactable or point stands in (its turned extent), if any. */
export const roomAt = (rooms: readonly ShipRoomLayout[], p: Point): ShipRoomLayout | null =>
  rooms.find((r) => p.x >= r.extent.x0 && p.x <= r.extent.x1 && p.y >= r.extent.y0 && p.y <= r.extent.y1) ?? null;

/** What a ship scene's rooms add to its walking ground: each room's floor, and its walls and solid furnishings as colliders. */
export type WalkExtras = { readonly surfaces: Scene['surfaces']; readonly colliders: Scene['colliders'] };

export const shipWalkExtras = (scene: Scene, rooms: readonly ShipRoomLayout[]): WalkExtras => {
  const surfaces: Scene['surfaces'][number][] = rooms.map((r) => ({ id: `${r.room.id}_floor`, x0: r.extent.x0, y0: r.extent.y0, x1: r.extent.x1, y1: r.extent.y1, h: r.extent.h }));
  const colliders: Scene['colliders'][number][] = rooms.flatMap((r) => r.worldWalls.map((w) => ({ x0: w.x0, y0: w.y0, x1: w.x1, y1: w.y1, id: w.id, height: w.height })));
  for (const i of scene.interactables) {
    if (i.size === undefined || i.solid === false) continue;
    const room = roomAt(rooms, i);
    const quarter = room !== null && (room.room.rotation / 90) % 2 === 1;
    const hx = (quarter ? i.size.d : i.size.w) / 2;
    const hy = (quarter ? i.size.w : i.size.d) / 2;
    colliders.push({ x0: i.x - hx, y0: i.y - hy, x1: i.x + hx, y1: i.y + hy, id: i.id, kind: 'fixture', height: i.size.h });
  }
  return { surfaces, colliders };
};

/** The post a crew member holds at a time of day, moored or under way (null: off deck then). */
export const crewPost = (scene: Scene, human: string, preset: string, flying: boolean): string | null => {
  const m = scene.crew?.members.find((x) => x.human === human);
  if (m === undefined) return null;
  const name = (flying ? m.flying : m.moored)[preset];
  return name !== undefined && scene.crew?.posts[name] !== undefined ? name : null;
};

/**
 * Places the airship's crew for the time of day and whether the ship flies (ADR 0033, `airship-crew.js` `roster`):
 * each member stands at its post (its height, facing and work) or is off deck; the posts held now stand as colliders
 * (the walking map's dynamic ones), except one the traveler stands in, which would trap him. A no-op on a scene
 * without a crew.
 */
export const applyCrew = (state: SceneState, preset: string, flying: boolean): void => {
  const crew = state.scene.crew;
  if (crew === undefined) return;
  const pad = state.scene.walk.pad;
  const p = state.player;
  const colliders: Scene['colliders'][number][] = [];
  for (const m of crew.members) {
    const r = state.residents.find((x) => x.human === m.human);
    if (r === undefined) continue;
    const name = crewPost(state.scene, m.human, preset, flying);
    const post = name === null ? undefined : crew.posts[name];
    r.moving = false;
    r.route = [];
    if (name === null || post === undefined) {
      r.present = false;
      r.post = null;
      r.action = null;
      continue;
    }
    r.present = true;
    r.post = name;
    r.action = post.action;
    r.x = post.x;
    r.y = post.y;
    r.h = post.h ?? state.map.heightAt(post.x, post.y);
    r.facing = post.facing;
    const c = post.collider;
    if (c !== undefined && !(p.x > c.x0 - pad && p.x < c.x1 + pad && p.y > c.y0 - pad && p.y < c.y1 + pad)) colliders.push({ x0: c.x0, y0: c.y0, x1: c.x1, y1: c.y1, id: `crew_${name}`, kind: 'fixture', height: c.height });
  }
  const now = state.map.dynamic();
  if (now.length !== colliders.length || now.some((c, i) => c.id !== colliders[i]?.id)) state.map.setDynamic(colliders);
  state.revision += 1;
};
